<template>
  <div>
    <title-row
      title="QR kód beállítás"
      :breadcrumb-items="[
        {
          text: 'QR kód igénylések',
          to: { name: 'QrCodeRequestList' },
        },
        {
          text: 'Küldés',
          disabled: true,
        },
      ]">
      <template #buttons>
        <title-row-button
          @click="importer.dialog = true"
          :disabled="!!selectedItems.length"
          title="Importálás Excel fájlból">
          <v-icon dark>mdi-file-excel</v-icon>
          Import
        </title-row-button>
        <title-row-button
          @click="save"
          title="Mentés"
          :loading="saving"
          :disabled="!isDirty">
          <v-icon left>mdi-content-save</v-icon>
          Mentés
        </title-row-button>
      </template>
    </title-row>

    <v-expansion-panels
      class="mb-4 rounded-xl elevation-0 sticky-header"
      accordion
      v-model="expansionPanelIndex">
      <v-expansion-panel>
        <v-expansion-panel-header>
          <span>
            <v-icon left>mdi-pencil-box-multiple</v-icon>
            Csoportos adatmódosítás
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row>
            <v-col cols="12" md="3" class="d-flex align-center">
              <v-autocomplete
                label="Jogosultság"
                v-model="multiEdit.accessLevelIds"
                :items="accessLevels"
                item-value="element_id"
                item-text="name"
                multiple
                small-chips
                hide-details
                dense
                @input="multiEdit.accessLevelIds.sort((a, b) => a - b)"
                @change="multiEdit.accessLevelIdsChanged = true"
                class="flex-grow-1" />
              <v-btn
                class="flex-grow-0"
                :disabled="!multiEdit.accessLevelIdsChanged || !selectedItems.length"
                icon
                tile
                color="primary"
                title="Kijelölt sorok módosítása"
                @click="setAccessLevelIds">
                <v-icon>mdi-check</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center">
              <v-datetime-picker
                label="Kezdő dátum"
                :time-picker="true"
                :date-picker="true"
                v-model="multiEdit.startDateTime"
                hide-details
                clearable
                dense
                @change="multiEdit.startDateTimeChanged = true"
                class="flex-grow-1" />
              <v-btn
                class="flex-grow-0"
                append-icon="mdi-check"
                :disabled="!multiEdit.startDateTimeChanged || !selectedItems.length"
                icon
                tile
                color="primary"
                title="Kijelölt sorok módosítása"
                @click="setStartDateTimes">
                <v-icon>mdi-check</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center">
              <v-masked-text-field
                class="text-right"
                label="Időtartam"
                :disabled="lockPeriods"
                v-model="multiEdit.period"
                :mask="$config.inputMasks.createNumberMask({ min: 1, max: 168 })"
                hide-details
                dense
                suffix="óra" />
              <v-btn
                :disabled="!multiEdit.periodChanged || !selectedItems.length"
                icon
                tile
                color="primary"
                title="Kijelölt sorok módosítása"
                @click="setPeriods">
                <v-icon>mdi-check</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header>
          <span>
            <v-icon left>mdi-email</v-icon>
            E-mail küldés
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row>
            <v-col cols="12" md="2" class="d-flex align-center">
              <v-select
                label="Kiküldés ideje"
                hide-details
                dense
                v-model="email.sendMode"
                :items="[
                  { value: 'immediate', text: 'Azonnal' },
                  { value: 'specific_time', text: 'Választott időpontban' },
                ]" />
            </v-col>

            <v-col
              v-if="email.sendMode === 'specific_time'"
              cols="12"
              md="2"
              class="d-flex align-center">
              <v-datetime-picker
                label="Időpont"
                dense
                class="flex-grow-1"
                :time-picker="true"
                :date-picker="true"
                :allowed-from="new Date().setMinutes(new Date().getMinutes() + 10)"
                v-model="email.sendDateTime"
                hide-details
                clearable
                elevation="0"
                placeholder="Válasszon"
                persistent-placeholder />
            </v-col>

            <v-col class="d-flex align-center">
              <v-btn
                color="primary"
                large
                depressed
                @click="sendEmails"
                :loading="sending"
                :disabled="!selectedItems.length ||
                  (email.sendMode === 'specific_time' && !email.sendDateTime)
                ">
                <v-icon left>mdi-email-fast</v-icon>
                Küldés
              </v-btn>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>


    <v-card flat class="rounded-xl overflow-hidden">
      <v-tabs v-model="tab" align-tabs="center" color="text--primary">
        <v-tabs-slider color="primary"></v-tabs-slider>
        <v-tab key="active_codes">Aktív kódok ({{ activeItems.length }})</v-tab>
        <v-tab key="sent_codes">Felhasznált kódok ({{ inactiveItems.length }})</v-tab>
        <!-- <v-tab v-if="$store.state.user.superadmin" key="filled">Minden felhasználó igénylései</v-tab> -->

        <v-tabs-items v-model="tab">
          <v-tab-item key="active_codes" :transition="false">
            <TabCodes
              :items="activeItems"
              v-model="selectedItems"
              show-select
              selectable-key="is_selectable"
              :loading="loading"
              :errors="errors"
              :access-levels="accessLevels" />
          </v-tab-item>

          <v-tab-item key="sent_codes" :transition="false">
            <TabCodes
              :items="inactiveItems"
              :loading="loading"
              :errors="errors"
              :access-levels="accessLevels" />
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </v-card>

    <v-dialog
      v-if="importer.dialog"
      :value="true"
      persistent
      max-width="480px">
      <v-card>
        <v-card-title>
          <v-icon left>mdi-file-import</v-icon>
          Vonalkód importálás
        </v-card-title>
        <v-card-text>
          <v-btn
            href="/assets/doc/vonalkod_import_sablon.xls"
            download
            large
            depressed
            block>
            <v-icon left>mdi-file-download</v-icon>
            Sablon XLS fájl letöltése
          </v-btn>

          <v-divider class="ma-4"></v-divider>

          <v-file-input
            label="Vonalkódokat tartalmazó XLS fájl feltöltése"
            filled
            prepend-icon=""
            prepend-inner-icon="mdi-file-upload"
            v-model="importer.file"
            :error-messages="errors.file"></v-file-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="importer.dialog = false">Mégse</v-btn>
          <v-btn
            color="primary"
            depressed
            :disabled="importer.loading"
            :loading="importer.loading"
            @click="importCodes">
            Importálás
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import TabCodes from './TabCodes.vue'

export default {
  components: { TabCodes },

  data() {
    return {
      importer: {
        loading: false,
        dialog: false,
        file: null,
      },
      multiEdit: {
        accessLevelIds: [],
        accessLevelIdsChanged: false,
        startDateTime: null,
        startDateTimeChanged: false,
        period: null,
        periodChanged: false,
        lockPeriod: false,
        show: false,
      },
      expansionPanelIndex: null,
      errors: {},
      accessLevels: [],
      selectedItems: [],
      loading: true,
      saving: false,
      sending: false,
      snapshot: [],
      lockPeriods: false,
      email: {
        sendMode: 'immediate',
        sendDateTime: null,
      },
      dataTable: {
        items: [],
        options: {
          itemsPerPage: 25,
        },
        headers: [
          { text: 'Rf ID / Szám', value: 'barcode_number' },
          { text: 'Kedvezményezett neve', value: 'name' },
          { text: 'Kedvezményezett email címe', value: 'email' },
          { text: 'Rendszám', value: 'plate', width: 200 },
          { text: 'Jogosultság', value: 'permissions' },
          { text: 'Kezdő dátum', value: 'start_date_time', width: 210 },
          { text: 'Időtartam (óra)', value: 'period', width: 120 },
        ],
      },
      tab: 0,
    };
  },

  watch: {
    'multiEdit.accessLevelIds'(curVal, prevVal) {
      this.multiEdit.accessLevelIdsChanged = curVal !== prevVal;
    },
    'multiEdit.startDateTime'(curVal, prevVal) {
      this.multiEdit.startDateTimeChanged = curVal !== prevVal;
    },
    'multiEdit.period'(curVal, prevVal) {
      this.multiEdit.periodChanged = curVal !== prevVal && (curVal !== '' || prevVal !== null);
    },
  },

  async mounted() {
    await this.fetchCodes();
    if (this.activeItems.length === 0) {
      this.tab = 1;
    }
  },

  methods: {
    async fetchCodes() {
      this.loading = true;
      const response = await this.$http.post(
        `qr-code-requests/codes/list/${this.$route.params.id || 0}`,
        this.postData
      );
      this.accessLevels = response.access_levels;

      if (this.$store.state.user.barcode_period_fix) {
        this.lockPeriods = true;
        this.multiEdit.period = response.qr_codes[0]?.period;
      }
      this.multiEdit.accessLevelIdsChanged = false;
      this.multiEdit.startDateTimeChanged = false;
      this.multiEdit.periodChanged = false;
      this.multiEdit.show = true;

      this.dataTable.items = response.qr_codes;
      this.selectedItems = this.lodash.intersectionWith(
        this.dataTable.items,
        this.selectedItems,
        (a, b) => a.id === b.id
      );
      this.snapshot = this.lodash.cloneDeep(this.dataTable.items);
      this.loading = false;
    },

    async importCodes() {
      this.errors = {};
      this.importer.loading = true;
      try {
        const formData = new FormData();
        formData.append('file', this.importer.file);
        formData.append('request_id', this.$route.params.id);

        const response = await this.$http.post('qr-code-requests/codes/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (Object.keys(response.errors).length) {
          this.errors = response.errors;
        }
        if (response.status === 'OK') {
          this.importer.dialog = false;
          this.importer.file = null;
          this.importer.loading = false;
          this.fetchCodes();
          this.$dialog.notify.success('Sikeres importálás');
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.importer.loading = false;
      }
    },

    async save() {
      this.errors = {};
      this.saving = true;
      try {
        const response = await this.$http.post('qr-code-requests/codes/save', {
          codes: this.dataTable.items,
        });

        if (Object.keys(response.errors).length) {
          this.errors = response.errors;
        } else {
          this.$dialog.notify.success('Sikeres mentés');
          await this.fetchCodes();
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.saving = false;
      }
    },

    async sendEmails() {
      if (this.isDirty) {
        await this.save();
      }
      this.sending = true;
      this.errors = {};
      try {
        const response = await this.$http.post('qr-code-requests/codes/email', {
          codes: this.selectedItems,
          send_delayed: this.email.sendMode === 'immediate' ? false : this.email.sendDateTime,
        });
        if (Object.keys(response.errors).length) {
          this.errors = response.errors;
          this.$dialog.notify.error('Sikertelen küldés!');
        } else if (response.status === 'OK') {
          this.selectedItems = [];
          this.fetchCodes();
          this.$dialog.notify.success('Sikeres küldés');
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.sending = false;
      }
    },

    filterOnylUnusedCodes(array) {
      return array.filter((item) => !item.email_sent);
    },

    isSelected(item) {
      return this.selectedItems.map((e) => e.id).includes(item.id);
    },

    setAccessLevelIds() {
      for (const item of this.selectedItems) {
        item.access_level_ids = this.multiEdit.accessLevelIds;
      }
      this.multiEdit.accessLevelIdsChanged = false;
    },

    setStartDateTimes() {
      for (const item of this.selectedItems) {
        item.start_date_time = this.multiEdit.startDateTime;
      }
      this.multiEdit.startDateTimeChanged = false;
    },

    setPeriods() {
      for (const item of this.selectedItems) {
        item.period = this.multiEdit.period;
        item.key = item.period;
      }
      this.multiEdit.periodChanged = false;
      this.$forceUpdate();
    },
  },

  computed: {
    activeItems() {
      return this.dataTable.items.filter((item) => item.email_sent == 0);
    },
    inactiveItems() {
      return this.dataTable.items.filter((item) => item.email_sent == 1);
    },
  },

  asyncComputed: {
    isDirty: {
      get() {
        return this.$config.comparators.isDirty(this.dataTable.items, this.snapshot);
      },
      watch: ['dataTable.items'],
    },
    // qrCodes: {
    //   async get() {
    //     this.loading = true;
    //     const response = await this.$http.post(
    //       `qr-code-requests/codes/list/${this.$route.params.id || 0}`,
    //       this.postData
    //     );
    //     this.accessLevels = response.access_levels;
    //     if (this.$store.state.user.barcode_period_fix) {
    //       this.lockPeriods = true;
    //       this.multiEdit.period = response.qr_codes[0]?.period;
    //     }
    //     this.multiEdit.accessLevelIdsChanged = false;
    //     this.multiEdit.startDateTimeChanged = false;
    //     this.multiEdit.periodChanged = false;
    //     this.multiEdit.show = true;
    //     this.loading = false;
    //     this.snapshot = this.lodash.cloneDeep(response.qr_codes);
    //     this.selectedItems = this.lodash.intersectionWith(this.dataTable.items, this.selectedItems, (a, b) => a.id === b.id)
    //     return response.qr_codes;
    //   },
    //   default: [],
    // },
  },
};
</script>

<style lang="scss" scoped>
::v-deep #overflow-btn {
  flex-grow: 0 !important;
  width: 0px !important;
  min-width: 0px !important;
}

.sticky-header {
  position: sticky;
  top: 128px;
  z-index: 10;
}
</style>
